import { Component, EventEmitter, inject, Inject, OnInit, Output } from '@angular/core';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalRef, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { CustomFieldsService } from 'src/app/private/services/custom-fields.service';
import { CustomFieldDto } from 'src/app/shared/dto/custom-field.dto';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomFieldsPropertiesDto } from 'src/app/shared/dto/custom-fields-properties.dto';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { CustomFieldsRestrictionsDto } from 'src/app/shared/dto/custom-fields-restrictions.dto';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { FieldTypesEnum } from 'src/app/shared/enums/field-types.enum';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { CustomFieldsValuesService } from 'src/app/private/services/custom-fields-values.service';
import { CustomFieldValueDto } from 'src/app/shared/dto/custom-field-value.dto';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { FinalProductDto } from 'src/app/shared/dto/final-product.dto';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';

@Component({
    selector: 'app-modal-quality-control',
    standalone: true,
    imports: [
        FormsModule,
        NzDividerModule,
        NzSwitchModule,
        NzRadioModule,
        NzSelectModule,
        NzModalTitleDirective,
        NzModalFooterDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzMessageModule,
        NzFormModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './modal-quality-control.component.html',
    styleUrl: './modal-quality-control.component.scss'
})
export class ModalQualityControlComponent implements OnInit {
	@Output() emitService = new EventEmitter();

	customFields: CustomFieldDto[] = [];
	typesEnum = FieldTypesEnum;
	infinity = Infinity;
	finalProductSelected?: number;

	isLoading: boolean = true;
	isSaving: boolean = false;

	private readonly modalRef = inject(NzModalRef);
	private readonly customFieldsService = inject(CustomFieldsService);
	private readonly customFieldsValuesService = inject(CustomFieldsValuesService);
	private readonly userLoggedService = inject(UserLoggedService);
	private readonly message = inject(NzMessageService);

	constructor(@Inject(NZ_MODAL_DATA) public data: { nzTitle: string, productionOrderProcessId: number, finalProducts: FinalProductDto[] }) { }

	async ngOnInit() {
	    try {
	        if (this.data.finalProducts.length == 1) {
	            this.finalProductSelected = this.data.finalProducts[0].id;
	        }
	        this.customFields = await this.customFieldsService.findByEntity('production.orders.process.detail.quality.modal');
	        this.customFields = this.customFields.map((cf) => {
	            if (cf.properties) {
	                cf.propertiesFormated = JSON.parse(cf.properties as string) as CustomFieldsPropertiesDto;
	                if (cf.propertiesFormated.options) {
	                    cf.propertiesFormated.optionsFormatted = cf.propertiesFormated.options.split(',');
	                }
	            }

	            if (cf.restrictions) {
	                cf.restrictionsFormated = JSON.parse(cf.restrictions as string) as CustomFieldsRestrictionsDto;
	            }

	            return cf;
	        });
	        this.isLoading = false;
	    } catch (error) {
	        this.isLoading = false;
	        throw error;
	    }
	}

	async handleCancel() {
	    this.modalRef.close();
	    this.emitService.emit();
	}

	async submitForm() {
		try {
			this.isSaving = true;
			if (!this.finalProductSelected) {
				this.isSaving = false;
				this.message.error('Indique el producto examinado');
				return;
			}

			if (!this.isValidCustomObjects()) {
				this.isSaving = false;
				return;
			}

			const customFieldsValues: CustomFieldValueDto[] = [];

			for (const customField of this.customFields) {
				const customFieldValueDto: CustomFieldValueDto = {
					customFieldId: customField.id!,
					productionOrderProcessId: this.data.productionOrderProcessId,
					finalProductId: this.finalProductSelected,
					createdById: this.userLoggedService.userLogged.id,
					value: customField.value?.toString()
				};
				customFieldsValues.push(customFieldValueDto);
			}

			await this.customFieldsValuesService.create(customFieldsValues);

			this.isSaving = false;
			this.message.success('Control de calidad guardado correctamente');
			this.modalRef.close();
			this.emitService.emit();
		} catch (e: any) {
            this.isSaving = false;
            this.message.error(e.message);
        }

	}

	isValidCustomObjects(): boolean {
	    for (const cf of this.customFields) {
	        if (cf.propertiesFormated?.type === FieldTypesEnum.INPUT_NUMBER && cf.value != null && cf.restrictionsFormated != null) {
	            if (cf.restrictionsFormated.max && (+cf.value) > cf.restrictionsFormated.max) {
	                this.message.error(`El campo ${cf.propertiesFormated?.name} debe ser menor de ${cf.restrictionsFormated.max}`);
	                return false;
	            }

	            if (cf.restrictionsFormated.min && (+cf.value) < cf.restrictionsFormated.min) {
	                this.message.error(`El campo ${cf.propertiesFormated?.name} debe ser mayor de ${cf.restrictionsFormated.min}`);
	                return false;
	            }
	        }

	        if (cf.restrictionsFormated == null || !cf.restrictionsFormated.required || cf.propertiesFormated?.type === FieldTypesEnum.CHECKBOX) {
	            if (cf.propertiesFormated?.type === FieldTypesEnum.CHECKBOX && cf.value == null) {
	                cf.value = false;
	            }
	            continue;
	        }

	        if (cf.restrictionsFormated.required && (cf.value == null || cf.value == '')) {
	            if (cf.propertiesFormated?.type === FieldTypesEnum.MULTISELECT && cf.value != null && (cf.value).length === 0) {
	                this.message.error(`El campo ${cf.propertiesFormated?.name} es obligatorio`);
	                return false;
	            }

	            this.message.error(`El campo ${cf.propertiesFormated?.name} es obligatorio`);
	            return false;
	        }
	    }
	    return true;
	}
}
